import * as React from 'react';
import { connect, type ConnectedProps } from 'react-redux';
import { compose } from 'redux';

import * as ReduxFormKeys from 'af-constants/reduxForms';

import EmailCreate from 'af-components/EditableOrganizationEmailPill/EmailCreate';
import EmailPillList from 'af-components/EditableOrganizationEmailPill/EmailPillList';

import * as OrganizationContactActions from 'af-actions/organizationContact';

import { RegistrationAndDeactivationNotificationContactFM } from './formModel';
import styles from './styles.module.scss';
import { NOTIFICATION_TYPE } from '../constants';

interface EmailContact {
	id: number;
	email: string;
}

type Props = ConnectedProps<typeof connector>;

const EmailList: React.FC<Props> = (props) => {

	const {
		findOrganizationContacts,
		createOrganizationContact,
		deleteOrganizationContact,
		updateOrganizationContact,
	} = props;

	const [emails, setEmails] = React.useState<EmailContact[]>([]);

	const fetchOrganizationContacts = React.useCallback(async () => {
		const contacts = await findOrganizationContacts(NOTIFICATION_TYPE);
		setEmails(contacts.emailContacts);
	}, [findOrganizationContacts]);

	React.useEffect(() => {
		fetchOrganizationContacts();
	}, [fetchOrganizationContacts]);

	const createEmailContact = React.useCallback(async (form: RegistrationAndDeactivationNotificationContactFM) => {
		await createOrganizationContact({ email: form.email, type: NOTIFICATION_TYPE });
		await fetchOrganizationContacts();
	}, [createOrganizationContact, fetchOrganizationContacts]);

	const deleteEmailContact = React.useCallback(async (id: number) => {
		await deleteOrganizationContact(id);
		await fetchOrganizationContacts();
	}, [deleteOrganizationContact, fetchOrganizationContacts]);

	const updateEmailContact = React.useCallback(async (id: number, form: RegistrationAndDeactivationNotificationContactFM) => {
		await updateOrganizationContact(
			id,
			RegistrationAndDeactivationNotificationContactFM.toUpdateRM(form)
		);
		await fetchOrganizationContacts();
	}, [fetchOrganizationContacts, updateOrganizationContact]);

	return (
		<>
			{!emails.length && <div className="m-t-m m-l-l">Add an email to start sending notifications</div>}
			<div className={styles['email-list']}>
				<EmailPillList
					deleteEmailContact={deleteEmailContact}
					emails={emails}
					formKey={ReduxFormKeys.ORGANIZATION_REGISTRATION_AND_DEACTIVATION_NOTIFICATION_CONTACT_UPDATE}
					updateEmailContact={updateEmailContact}
				/>
			</div>
			<EmailCreate createEmail={createEmailContact} form={ReduxFormKeys.ORGANIZATION_REGISTRATION_AND_DEACTIVATION_NOTIFICATION_CONTACT_CREATE} />
		</>
	);
};

function mapDispatchToProps() {
	return {
		findOrganizationContacts: OrganizationContactActions.findOrganizationContacts,
		createOrganizationContact: OrganizationContactActions.createOrganizationContact,
		deleteOrganizationContact: OrganizationContactActions.deleteOrganizationContact,
		updateOrganizationContact: OrganizationContactActions.updateOrganizationContact,
	};
}

const connector = connect(null, mapDispatchToProps());

const enhance = compose<React.ComponentType>(
	React.memo,
	connector
);

export default enhance(EmailList);
